import dayjs from 'dayjs';
import DOMPurify from 'isomorphic-dompurify';
import { CampaignParameters, operationalDetailsType } from '../../organisms/LocalServices/LocalServices.types';
import { sevenRooms, openTable, restref, lang, defaultENUS } from './constants/constants';
import { isAppleDevice } from './deviceDetection';
/*
To convert phone number format for DE domain
*/
export const canUseDOM = !!(typeof window !== 'undefined' && window.document);

export const convertPhoneNumberToDE = (phoneNumber: string): string => {
  let result = '';
  const num = phoneNumber.split(' ');
  let i = 0;
  if (num.length > 1) {
    while (i < num.length - 1) {
      if (i !== 0 && i < num.length - 1) num[i] += '-';
      else num[i] += ' ';
      i++;
    }
    result = num.join('');
  } else {
    const n = num.toString();
    result = `${n.slice(0, 2)} ${n.slice(2, 5)}-${n.slice(5, 8)}-${n.slice(8, n.length)}`;
  }
  return result;
};

export const formatCurrency = (amount: number, locale: string, currency: string): string => {
  const pricingFormat = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'code',
  });

  return pricingFormat.format(amount).replace(currency, '').trim();
};

export const sanitize = (text: string): string | TrustedHTML => {
  return (
    DOMPurify.sanitize(text?.toString().replace(/<\/?p>/g, ''), {
      ADD_ATTR: ['target'],
      RETURN_DOM: true,
    }) as HTMLElement
  ).innerHTML;
};

export const sanitizePureHTML = (text: string): string | TrustedHTML => {
  return (
    DOMPurify.sanitize(text?.toString(), {
      ADD_ATTR: ['target'],
      RETURN_DOM: true,
    }) as HTMLElement
  ).innerHTML;
};

export const updateDataLayerProperties = (
  dataLayerProperties: { [key: string]: unknown },
  userExperience: string,
  enrollmentcode: string
) => {
  if (canUseDOM) {
    const dataLayer = window.dataLayer ?? {};
    Object.keys(dataLayerProperties).forEach(key => {
      dataLayer[key] = dataLayerProperties[key];
    });
    dataLayer['page_portal_age_experience'] = userExperience;
    dataLayer['mr_enrollment_source'] = enrollmentcode ?? '';
    window.dataLayer = {
      ...dataLayer,
    };
  }
};

const ppv5SameTabStates = ['preconnect', 'connect'];

export const isPpv5AppleDevice = (): boolean => isAppleDevice();

export const isPpv5PreConnectOrConnectState = (state?: string): boolean => {
  const normalizedState =
    state
      ?.toString()
      ?.toLowerCase()
      ?.replace(/[-_\s]/g, '') ?? '';

  return ppv5SameTabStates.includes(normalizedState);
};

export const openPpv5LinkInSameTab = (state?: string, sameTab?: unknown, cna?: unknown): boolean => {
  console.log('state', state);
  console.log('isPpv5PreConnectOrConnectState', isPpv5PreConnectOrConnectState(state));
  return Boolean(isPpv5AppleDevice() ? true : sameTab || cna);
};

export const getPpv5LinkTarget = (
  state?: string,
  sameTab?: unknown,
  cna?: unknown,
  defaultTarget?: string
): string | undefined => {
  return openPpv5LinkInSameTab(state, sameTab, cna) ? '_self' : defaultTarget;
};

export const sanitizeDatalayerPayload = (obj: dataLayerType) => {
  if (typeof obj !== 'object' || obj === null) return obj;

  const cleanedObj: { [key: string]: unknown } = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value === '' || value === null || value === undefined) {
      continue; // Skip keys with "", null, or undefined
    }
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      const nested = sanitizeDatalayerPayload(value as dataLayerType);
      // Only add if nested object is not empty
      if (Object.keys(nested ?? {}).length > 0) {
        cleanedObj[key] = nested;
      }
    } else {
      cleanedObj[key] = value;
    }
  }

  return cleanedObj;
};

export const ternaryHandler = (value: unknown, defaultValue: string | boolean | unknown = '') => {
  return value ?? defaultValue;
};

export const updateDateFormat = (input: string, format = 'MM/DD/YYYY') => {
  if (input === '') {
    return '';
  }
  const parsedDate = dayjs(input);
  if (!parsedDate.isValid()) {
    return '';
  }
  return parsedDate.format(format);
};

export function accountAgeCalculator(datalayer: dataLayerType) {
  if (datalayer?.['memState'] === 'authenticated') {
    const joinDate = (ternaryHandler(datalayer?.['mr_prof_join_date']) as string).replaceAll('-', '/');
    const joinDateObj = new Date(joinDate);
    const today = new Date();

    //calculate time difference in milliseconds
    const diffInMs: number = today.getTime() - joinDateObj.getTime();

    //convert ms -> days (1 day = 86400000ms)
    const diffInDays = Math.round(diffInMs / 86400000);

    //ensure it's valid number
    if (!isNaN(diffInDays)) {
      return diffInDays.toString();
    }
    return '';
  }
  return '';
}

// check that seven rooms URL has valid format
export const isSevenRoomsUrlValid = (reservationUrl: string): boolean => {
  try {
    // Add protocol if missing
    const urlWithProtocol =
      reservationUrl.startsWith('http://') || reservationUrl.startsWith('https://')
        ? reservationUrl
        : `https://${reservationUrl}`;

    const url = new URL(urlWithProtocol);
    // const search_params = url.searchParams;
    // TODO: handle "venus" param --> how will it get added to URL?

    // accepts https and http protocol
    // accepts combination of a-z, 0-9 and hyphen for restaurantSoftwareId
    // accepts presence or absence of venues param with list of restaurantSoftwareId's
    const regex = /^https?:\/\/www\.sevenrooms\.com\/reservations\/[a-z0-9-]+(?:\?venues=[a-z0-9-]+(?:,[a-z0-9-]+)*)?$/;

    return regex.test(url.href);
  } catch (error) {
    // Return false if URL is invalid
    return false;
  }
};

// check that open table URL is valid
export const isOpenTableUrlValid = (reservationUrl: string): boolean => {
  try {
    // Add protocol if missing
    const urlWithProtocol =
      reservationUrl.startsWith('http://') || reservationUrl.startsWith('https://')
        ? reservationUrl
        : `https://${reservationUrl}`;

    const url = new URL(urlWithProtocol);
    const search_params = url.searchParams;

    // accepts https and http protocol
    const regex = /^https?:\/\/www\.opentable\.com\/restaurant\/profile\/(\d+)\/reserve\?restref=\1$/;

    const routeSegments = url.pathname.split('/').filter(Boolean);

    return regex.test(url.href) && routeSegments[2] === search_params.get('restref');
  } catch (error) {
    // Return false if URL is invalid
    return false;
  }
};

export const getUpdatedReservationUrl = (
  campaignParams: CampaignParameters,
  reservationUrl: string,
  operationalDetails: operationalDetailsType | undefined
) => {
  try {
    // Add protocol if missing
    const urlWithProtocol =
      reservationUrl.startsWith('http://') || reservationUrl.startsWith('https://')
        ? reservationUrl
        : `https://${reservationUrl}`;

    let url = new URL(urlWithProtocol);
    const search_params = url.searchParams;

    // check URL is valid, restaurantSoftware = "SevenRooms", and restuarantSoftwareId is present
    if (
      isSevenRoomsUrlValid(reservationUrl) &&
      operationalDetails &&
      operationalDetails?.restaurantSoftware === sevenRooms &&
      operationalDetails?.restaurantSoftwareId
    ) {
      // check that source param value (param1Value) is present and not an empty string
      // changes in AEM code will restrict param1Value to list of pre-defined tracking parameters
      const sourceParameter =
        campaignParams?.param1Value && campaignParams?.param1Value !== '' ? campaignParams?.param1Value : '';

      if (sourceParameter) {
        if (url?.pathname.endsWith('/'))
          url = new URL(`${url?.origin}${url?.pathname}${sourceParameter}${url?.search}`);
        else url = new URL(`${url?.origin}${url?.pathname}/${sourceParameter}${url?.search}`);
        return url?.href;
      }
      return url?.href;
    }

    // check URL is valid, restaurantSoftware = 'OpenTable' and restaurantSoftwareId is present
    if (
      isOpenTableUrlValid(reservationUrl) &&
      operationalDetails &&
      operationalDetails?.restaurantSoftware === openTable &&
      operationalDetails?.restaurantSoftwareId
    ) {
      if (!search_params?.has(restref) && operationalDetails?.restaurantSoftwareId) {
        url.searchParams.set(restref, operationalDetails?.restaurantSoftwareId);
      }
      url.searchParams.set(lang, defaultENUS);
      if (
        campaignParams?.param1 &&
        campaignParams?.param1 !== '' &&
        campaignParams?.param1Value &&
        campaignParams?.param1Value !== ''
      )
        url.searchParams.set(campaignParams?.param1, campaignParams?.param1Value);
      if (
        campaignParams?.param2 &&
        campaignParams?.param2 !== '' &&
        campaignParams?.param2Value &&
        campaignParams?.param2Value !== ''
      )
        url.searchParams.set(campaignParams?.param2, campaignParams?.param2Value);
      url.search = search_params.toString();
      return url?.href;
    } else return url?.href;
  } catch (error) {
    // Return original URL if parsing fails
    return reservationUrl;
  }
};
